// UGC videos section (sections/ugc-videos.liquid): grid videos play muted while on screen, and clicking one opens it
// in a popup with sound, its caption and its date.

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const supportsClosedBy = 'closedBy' in HTMLDialogElement.prototype;

class UgcVideos extends HTMLElement {
  #controller;
  #observer;
  #visibleVideos = new Set();

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.dialog = this.querySelector('dialog');
    this.content = this.querySelector('[data-ugc-content]');

    this.addEventListener('click', this.#onClick.bind(this), { signal });
    this.dialog.addEventListener('close', this.#onClose.bind(this), { signal });
    reducedMotion.addEventListener('change', () => this.#syncGridPlayback(), { signal });

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#visibleVideos.add(entry.target);
        } else {
          this.#visibleVideos.delete(entry.target);
        }
      }

      this.#syncGridPlayback();
    }, { threshold: 0.25 });

    this.querySelectorAll('.ugc-videos__tile-video').forEach((video) => this.#observer.observe(video));
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#visibleVideos.clear();
  }

  get #modalVideo() {
    return this.content.querySelector('video');
  }

  #onClick(event) {
    const tile = event.target.closest('[data-ugc-open]');

    if (tile) {
      return this.#open(tile);
    }

    if (!this.dialog.contains(event.target)) {
      return;
    }

    if (event.target.closest('[data-ugc-close]')) {
      return this.dialog.close();
    }

    if (event.target.closest('[data-ugc-mute]')) {
      return this.#setMuted(!this.#modalVideo.muted);
    }

    if (event.target === this.#modalVideo) {
      if (this.#modalVideo.paused) {
        const loader = this.content.querySelector('[data-ugc-loader]');
        if (this.#modalVideo.readyState < 3) {
          loader?.classList.add('is-loading');
        }
        return this.#modalVideo.play().catch(() => {
          loader?.classList.remove('is-loading');
        });
      }

      return this.#modalVideo.pause();
    }

    // Light dismiss for browsers without <dialog closedby> support: a click on the backdrop targets the dialog itself.
    if (!supportsClosedBy && event.target === this.dialog) {
      const rect = this.dialog.getBoundingClientRect();
      const isInside = rect.top <= event.clientY && event.clientY <= rect.bottom && rect.left <= event.clientX && event.clientX <= rect.right;

      if (!isInside) {
        this.dialog.close();
      }
    }
  }

  #open(tile) {
    const template = tile.closest('.ugc-videos__item')?.querySelector('[data-ugc-template]');

    if (!template) {
      return;
    }

    this.content.replaceChildren(template.content.cloneNode(true));
    this.dialog.showModal();
    this.#syncGridPlayback();

    const video = this.#modalVideo;
    const loader = this.content.querySelector('[data-ugc-loader]');

    if (!video) {
      return;
    }

    const showLoader = () => loader?.classList.add('is-loading');
    const hideLoader = () => loader?.classList.remove('is-loading');

    video.addEventListener('playing', hideLoader);
    video.addEventListener('timeupdate', () => {
      if (video.currentTime > 0 && !video.paused) {
        hideLoader();
      }
    });
    video.addEventListener('waiting', showLoader);
    video.addEventListener('stalled', showLoader);
    video.addEventListener('pause', () => {
      if (video.readyState >= 3) {
        hideLoader();
      }
    });
    video.addEventListener('error', hideLoader);

    // Opening is a user click, so sound is normally allowed. If the browser refuses, fall back to muted playback.
    this.#setMuted(false);
    video.play().catch(() => {
      this.#setMuted(true);
      video.play().catch(() => {
        hideLoader();
      });
    });
  }

  #onClose() {
    this.#modalVideo?.pause();
    this.content.replaceChildren();
    this.#syncGridPlayback();
  }

  #setMuted(muted) {
    const video = this.#modalVideo;
    const button = this.content.querySelector('[data-ugc-mute]');

    if (video) {
      video.muted = muted;
    }

    if (button) {
      button.setAttribute('aria-pressed', String(muted));
      button.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
    }
  }

  #syncGridPlayback() {
    const canPlay = !reducedMotion.matches && !this.dialog.open;

    this.querySelectorAll('.ugc-videos__tile-video').forEach((video) => {
      if (canPlay && this.#visibleVideos.has(video)) {
        video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    });
  }
}

if (!customElements.get('ugc-videos')) {
  customElements.define('ugc-videos', UgcVideos);
}
